import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { BRAND_MINT, COMPLIANCE_BADGE, badgePill } from '@/lib/utils'
import {
  getPendingVerificationOrgsApi,
  approveOrgVerificationApi,
  rejectOrgVerificationApi,
  getAdminSignatoriesApi,
  type PendingVerificationOrg,
  type Signatory,
} from '@/lib/api'
import {
  ShieldCheckIcon,
  LoaderIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingIcon,
  HashIcon,
  MapPinIcon,
  GlobeIcon,
  AlertTriangleIcon,
  FileSignatureIcon,
  UserIcon,
} from 'lucide-react'

/**
 * Color treatment for each verification status.
 */
const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: {
    label: 'Pending',
    color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  active: {
    label: 'Active',
    color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-destructive/10 text-destructive',
  },
  inactive: {
    label: 'Inactive',
    color: 'bg-muted text-muted-foreground',
  },
}

export const Route = createFileRoute('/dashboard/admin/org-verification')({
  component: () => {
    const [orgs, setOrgs] = useState<PendingVerificationOrg[]>([])
    const [loading, setLoading] = useState(true)
    const [actioning, setActioning] = useState<number | null>(null)

    // U22 — read-only compliance panel: e-signature status per org
    const [signatories, setSignatories] = useState<Record<number, Signatory[]>>({})
    const [signatoriesLoaded, setSignatoriesLoaded] = useState<Record<number, boolean>>({})
    const [signatoriesFailed, setSignatoriesFailed] = useState<Record<number, boolean>>({})

    // Confirmation dialog state
    const [confirmOrg, setConfirmOrg] = useState<PendingVerificationOrg | null>(null)
    const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null)

    useEffect(() => {
      let cancelled = false

      async function load() {
        try {
          const res = await getPendingVerificationOrgsApi()
          if (!cancelled) setOrgs(res.data)

          // U22: fetch the e-signature status for every org in the queue so
          // admins can confirm a binding signature exists before approving.
          const settled = await Promise.allSettled(
            res.data.map(async (o) => {
              const s = await getAdminSignatoriesApi(o.org_id)
              return { orgId: o.org_id, data: s.data }
            }),
          )
          const nextSignatories: Record<number, Signatory[]> = {}
          const nextLoaded: Record<number, boolean> = {}
          const nextFailed: Record<number, boolean> = {}
          settled.forEach((r, i) => {
            const orgId = res.data[i].org_id
            // Mark loaded even on rejection so the panel resolves instead of
            // spinning forever — failed orgs get an explicit unavailable state.
            nextLoaded[orgId] = true
            if (r.status === 'fulfilled') {
              nextSignatories[orgId] = r.value.data
            } else {
              nextFailed[orgId] = true
            }
          })
          if (!cancelled) {
            setSignatories(nextSignatories)
            setSignatoriesLoaded(nextLoaded)
            setSignatoriesFailed(nextFailed)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load organizations',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => {
        cancelled = true
      }
    }, [])

    const openConfirm = (org: PendingVerificationOrg, action: 'approve' | 'reject') => {
      setConfirmOrg(org)
      setConfirmAction(action)
    }

    const closeConfirm = () => {
      setConfirmOrg(null)
      setConfirmAction(null)
    }

    const handleConfirmAction = async () => {
      if (!confirmOrg || !confirmAction) return

      const orgId = confirmOrg.org_id
      setActioning(orgId)
      closeConfirm()

      try {
        if (confirmAction === 'approve') {
          await approveOrgVerificationApi(orgId)
          setOrgs((prev) => prev.filter((o) => o.org_id !== orgId))
          toast.success(`"${confirmOrg.org_name}" approved and is now active`)
        } else {
          await rejectOrgVerificationApi(orgId)
          setOrgs((prev) => prev.filter((o) => o.org_id !== orgId))
          toast.success(`"${confirmOrg.org_name}" verification rejected`)
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : `Failed to ${confirmAction} organization`,
        )
      } finally {
        setActioning(null)
      }
    }

    // --------------- Loading state ---------------

    if (loading) {
      return (
        <div className="mx-auto max-w-3xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <h1 className="text-2xl font-semibold">Organization verification</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading organizations…</p>
          </div>
        </div>
      )
    }

    // --------------- Empty state ---------------

    if (orgs.length === 0) {
      return (
        <div className="mx-auto max-w-3xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <div>
                <h1 className="text-2xl font-semibold">Organization verification</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Review and approve organizations waiting for verification.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <CheckCircleIcon className="size-10 text-emerald-500" />
            <p className="text-sm font-medium text-foreground">
              All caught up!
            </p>
            <p className="text-sm text-muted-foreground">
              No organizations are pending verification.
            </p>
          </div>
        </div>
      )
    }

    // --------------- Main list ---------------

    return (
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Organization verification</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Review and approve organizations waiting for verification.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {orgs.map((org) => {
            const statusInfo = STATUS_LABELS[org.verified_status] ?? {
              label: org.verified_status,
              color: 'bg-muted text-muted-foreground',
            }

            return (
              <div
                key={org.org_id}
                className="rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <BuildingIcon className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="truncate text-base font-semibold">
                          {org.org_name}
                        </h2>
                        <span className={badgePill(statusInfo.color)}>
                          <ClockIcon className="size-3" />
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {org.vat_number && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <HashIcon className="size-3.5 shrink-0" />
                          <span className="truncate">VAT: {org.vat_number}</span>
                        </div>
                      )}
                      {org.registration_number && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <HashIcon className="size-3.5 shrink-0" />
                          <span className="truncate">Reg. no: {org.registration_number}</span>
                        </div>
                      )}
                      {org.registered_address && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPinIcon className="size-3.5 shrink-0" />
                          <span className="truncate">{org.registered_address}</span>
                        </div>
                      )}
                      {org.country_name && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <GlobeIcon className="size-3.5 shrink-0" />
                          <span>{org.country_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ClockIcon className="size-3.5 shrink-0" />
                        <span>
                          Created{' '}
                          {new Date(org.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>

                    {/* U22 — signatory / e-signature status (read-only) */}
                    <SignatoryStatus
                      signatories={signatories[org.org_id] ?? []}
                      loaded={Boolean(signatoriesLoaded[org.org_id])}
                      failed={Boolean(signatoriesFailed[org.org_id])}
                    />
                  </div>

                  <div className="flex shrink-0 items-start gap-2">
                    {org.verified_status === 'pending' ? (
                      <>
                        <Button
                          onClick={() => openConfirm(org, 'reject')}
                          disabled={actioning === org.org_id}
                          variant="destructive"
                          size="sm"
                          className="gap-1.5"
                        >
                          {actioning === org.org_id ? (
                            <LoaderIcon className="size-4 animate-spin" />
                          ) : (
                            <XCircleIcon className="size-4" />
                          )}
                          Reject
                        </Button>
                        <Button
                          onClick={() => openConfirm(org, 'approve')}
                          disabled={actioning === org.org_id}
                          size="sm"
                          className="gap-1.5"
                        >
                          {actioning === org.org_id ? (
                            <LoaderIcon className="size-4 animate-spin" />
                          ) : (
                            <ShieldCheckIcon className="size-4" />
                          )}
                          Approve
                        </Button>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
                        {org.verified_status === 'rejected' ? (
                          <XCircleIcon className="size-3.5" />
                        ) : (
                          <CheckCircleIcon className="size-3.5" />
                        )}
                        {statusInfo.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <p className="mt-4 text-xs text-muted-foreground text-center">
          {orgs.length} organization{orgs.length !== 1 ? 's' : ''} pending review
        </p>

        {/* ── Confirmation Dialog ── */}
        <Dialog
          open={confirmOrg !== null}
          onClose={closeConfirm}
          title={
            confirmAction === 'approve'
              ? 'Approve organization?'
              : 'Reject organization?'
          }
          description={
            confirmAction === 'approve'
              ? `This will activate "${confirmOrg?.org_name}" so they can perform payroll operations. They have filled in their registration details as part of their application.`
              : `This will reject "${confirmOrg?.org_name}"'s verification request. They will not be able to perform payroll operations until re-approved by an admin.`
          }
          footer={
            <>
              <Button variant="outline" size="sm" onClick={closeConfirm}>
                Cancel
              </Button>
              <Button
                variant={confirmAction === 'approve' ? 'default' : 'destructive'}
                size="sm"
                onClick={handleConfirmAction}
                className="gap-1.5"
              >
                {confirmAction === 'approve' ? (
                  <ShieldCheckIcon className="size-4" />
                ) : (
                  <AlertTriangleIcon className="size-4" />
                )}
                {confirmAction === 'approve' ? 'Approve & activate' : 'Yes, reject'}
              </Button>
            </>
          }
        />
      </div>
    )
  },
})

/**
 * U22 — read-only signatory / e-signature status panel for the verification
 * queue. Lets an admin confirm a binding in-app signature exists before
 * approving an organization (F5: `GET /admin/organizations/:id/signatories`).
 */
function SignatoryStatus({
  signatories,
  loaded,
  failed,
}: {
  signatories: Signatory[]
  loaded: boolean
  failed: boolean
}) {
  const signedRep = signatories.find((s) => s.typed_name)
  const activeRep = signatories.find((s) => s.is_active)

  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <FileSignatureIcon className="size-3.5" />
          Signatory / e-signature
        </p>
        {signedRep && (
          <span className={badgePill(COMPLIANCE_BADGE)}>
            <CheckCircleIcon className="size-3" />
            E-signed
          </span>
        )}
      </div>

      {!loaded ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <LoaderIcon className="size-3.5 animate-spin" />
          Loading signatory status…
        </div>
      ) : failed ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <AlertTriangleIcon className="size-3.5" />
          Couldn't load signatory status.
        </p>
      ) : signedRep ? (
        <div className="mt-2 space-y-1">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
            {signedRep.full_legal_name}
            <span className="font-normal text-muted-foreground">
              · {signedRep.job_title}
            </span>
            {signedRep.is_active && (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                Active
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            Signed as “{signedRep.typed_name}” ·{' '}
            {signedRep.signed_at
              ? new Date(signedRep.signed_at).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
              : '—'}
          </p>
          <p className="break-all font-mono text-[11px] text-muted-foreground">
            {signedRep.terms_version ?? '—'} ToS / {signedRep.dpa_version ?? '—'} DPA ·{' '}
            sha256:{signedRep.document_version_hash?.slice(0, 12)}…
          </p>
        </div>
      ) : activeRep ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangleIcon className="size-3.5" />
          {activeRep.full_legal_name} on file but not yet e-signed.
        </p>
      ) : (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <UserIcon className="size-3.5" />
          No signatory on file — no binding signature yet.
        </p>
      )}
    </div>
  )
}
